// utils/trackNavigation.js
// ─────────────────────────────────────────────────────────
// Extracted from the inline onClick in TrackDetail.jsx.
// Centralises the "where should the user go next?" logic
// so it can be tested independently and reused.
//
// PLACEMENT: src/utils/trackNavigation.js
// ─────────────────────────────────────────────────────────

/**
 * Given a track and enrollment, returns the next navigation
 * target for "Continue Learning".
 *
 * @param {Object} track       - Full track object (with phases)
 * @param {Object} enrollment  - UserTrackEnrollment object
 * @returns {{ type: string, phaseId: string, resourceIndex?: number } | null}
 *
 * Types:
 *   'resource'   → navigate to /tracks/:slug/resource/:phaseId/:resourceIndex
 *   'assessment' → navigate to /tracks/:slug/assessment/:phaseId
 *   'project'    → navigate to /tracks/:slug/project/:phaseId
 *   null         → nothing to navigate to (completed or no phases)
 */
export function getNextStep(track, enrollment) {
    if (!track?.phases?.length || !enrollment) return null;

    const currentPhaseData = track.phases.find(
        (p) => p.order === enrollment.currentPhase
    );
    if (!currentPhaseData) return null;

    const pp = currentPhaseData.phaseProgress;
    const completedIndices = pp?.completedResources || [];
    const resourceCount = currentPhaseData.resources?.length || 0;
    const hasAssessment = currentPhaseData.assessmentVideoIds?.length > 0;
    const quizScore = pp?.quizScore ?? null;

    // 1. Find first incomplete resource
    for (let i = 0; i < resourceCount; i++) {
        if (!completedIndices.includes(i)) {
            return {
                type: 'resource',
                phaseId: currentPhaseData._id,
                resourceIndex: i,
            };
        }
    }

    // 2. All resources done — check assessment
    if (hasAssessment && (quizScore === null || quizScore < 70)) {
        return {
            type: 'assessment',
            phaseId: currentPhaseData._id,
        };
    }

    // 3. Assessment passed (or none required) — check project
    if (currentPhaseData.project && !pp?.projectLogged) {
        return {
            type: 'project',
            phaseId: currentPhaseData._id,
        };
    }

    // 4. Everything done — fall back to first resource
    return {
        type: 'resource',
        phaseId: currentPhaseData._id,
        resourceIndex: 0,
    };
}

/**
 * Converts a getNextStep result into a URL string.
 *
 * @param {string} slug - Track slug
 * @param {{ type, phaseId, resourceIndex }} step
 * @returns {string}
 */
export function nextStepUrl(slug, step) {
    if (!step) return `/tracks/${slug}`;

    switch (step.type) {
        case 'resource':
            return `/tracks/${slug}/resource/${step.phaseId}/${step.resourceIndex ?? 0}`;
        case 'assessment':
            return `/tracks/${slug}/assessment/${step.phaseId}`;
        case 'project':
            return `/tracks/${slug}/project/${step.phaseId}`;
        default:
            return `/tracks/${slug}`;
    }
}